import { useEffect, useMemo, useState } from 'react';

import { groupMeetings } from './groupMeetings.ts';
import { getMockMeetings } from './mockMeetings.ts';
import type { MeetingDateRange, MeetingsEligibility, UseMeetingsResult } from './meetingTypes.ts';

const SIMULATED_LATENCY_MS = 400;

export type UseMeetingsOptions = {
	/** Date range to fetch meetings for. Defaults to "today" upstream. */
	dateRange?: MeetingDateRange;
	/**
	 * Test/story override for eligibility so all banner states can be exercised
	 * without real Loom settings. Defaults to 'eligible' in the mock.
	 */
	eligibilityOverride?: MeetingsEligibility;
};

/**
 * MOCK implementation of the Meetings data hook.
 *
 * This is the single seam between the UI and the data layer. When the live
 * Relay query against `loom_connectedCalendars.meetings` + `loom_meetingActionItems`
 * is ready (pending AGG scope confirmation), only this file is replaced — the
 * returned `UseMeetingsResult` contract stays identical.
 *
 * See tech spec: https://hello.atlassian.net/wiki/spaces/Growth/pages/7345509583
 */
export const useMeetings = (options: UseMeetingsOptions = {}): UseMeetingsResult => {
	const { dateRange, eligibilityOverride } = options;
	const [loading, setLoading] = useState(true);

	// Simulate an async fetch so skeleton/loading states are exercisable.
	useEffect(() => {
		setLoading(true);
		const timer = setTimeout(() => setLoading(false), SIMULATED_LATENCY_MS);
		return () => clearTimeout(timer);
	}, [dateRange?.rangeStart, dateRange?.rangeEnd]);

	const eligibility: MeetingsEligibility = eligibilityOverride ?? 'eligible';

	// Depend on the primitive range bounds (not the object reference) so an inline
	// `dateRange={{ ... }}` from the caller doesn't recompute on every render.
	// Mirrors the primitive deps used by the effect above.
	const rangeStart = dateRange?.rangeStart;
	const rangeEnd = dateRange?.rangeEnd;

	const groups = useMemo(() => {
		if (loading || eligibility !== 'eligible') {
			return [];
		}
		const range =
			rangeStart !== undefined && rangeEnd !== undefined ? { rangeStart, rangeEnd } : undefined;
		return groupMeetings(getMockMeetings(range));
	}, [loading, eligibility, rangeStart, rangeEnd]);

	const totalCount = useMemo(
		() => groups.reduce((sum, group) => sum + group.meetings.length, 0),
		[groups],
	);

	return {
		eligibility: loading ? 'loading' : eligibility,
		loading,
		groups,
		totalCount,
	};
};
